import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import { WaterfallBarType, WaterfallRowType } from "~/components/build/Show/lib/useWaterfallStore";
import MaybeLink from "~/components/build/Show/components/Waterfall/MaybeLink";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "~/components/shared/HoverCard";
import classNames from "classnames";

type WaterfallPopoverDurationProps = {
  duration: string;
  text: string;
  variant: "limited" | "waiting" | "dispatching" | "running";
};

function WaterfallPopoverDuration(props: WaterfallPopoverDurationProps) {
  const circleColor: Record<WaterfallPopoverDurationProps["variant"], string> = {
    limited: "var(--gray-700)",
    waiting: "var(--gray-500)",
    dispatching: "var(--orange-500)",
    running: "var(--green-500)",
  };

  const dashed = props.variant === "limited";

  return (
    <div className="grid grid-cols-[10px_auto_1fr] items-center gap-2 whitespace-nowrap">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
      >
        <circle
          cx="5"
          cy="5"
          r="4"
          stroke={circleColor[props.variant]}
          strokeWidth="1.5"
          strokeDasharray={dashed ? 2 : undefined}
        />
      </svg>
      <span className="monospace">{props.duration}</span>
      <span className="text-start">{props.text}</span>
    </div>
  );
}

type WaterfallChartBarProps = WaterfallBarType & {
  isLastBar: boolean;
  duration: string | undefined;
};

function WaterfallChartBar(props: WaterfallChartBarProps) {
  const outcomeBarColor = props.passed ? "var(--green-500)" : "var(--red-500)";

  return (
    <>
      {props.parent ? (
        <HoverCard openDelay={100} closeDelay={50}>
          <div
            data-testid="waterfall-chart-limited"
            style={{
              left: `${props.limited.start_percentage}%`,
              top: "calc(50% - 1px)",
              width: `${props.limited.percentage}%`,
              height: "10px",
            }}
            className="waterfall-chart__bar absolute items-center justify-center"
          >
            <div className="w-full border-b-2 border-dashed border-gray-700" />
          </div>
          <HoverCardTrigger asChild={true}>
            <div
              data-testid="waterfall-chart-bar"
              style={{
                left: `${props.active.start_percentage}%`,
                top: "calc(50% - 5px)",
                width: `${props.active.total_percentage}%`,
                height: "10px",
              }}
              className="waterfall-chart__bar absolute flex"
            >
              <div
                className="h-full w-full rounded-sm"
                style={{
                  backgroundColor: outcomeBarColor,
                }}
              />
            </div>
          </HoverCardTrigger>
          <HoverCardContent size="max-lg">
            <div className="charcoal-800">
              <WaterfallPopoverDuration
                text="Limited"
                duration={props.limited.duration}
                variant="limited"
              />
              <WaterfallPopoverDuration
                text="Active"
                duration={props.active.total_duration}
                variant="running"
              />
            </div>
          </HoverCardContent>
        </HoverCard>
      ) : (
        <HoverCard openDelay={100} closeDelay={50}>
          <div
            data-testid="waterfall-chart-limited"
            style={{
              left: `${props.limited.start_percentage}%`,
              top: "calc(50% - 1px)",
              width: `${props.limited.percentage}%`,
              height: "10px",
            }}
            className="waterfall-chart__bar absolute items-center justify-center"
          >
            <div className="w-full border-b-2 border-dashed border-gray-700" />
          </div>
          <div
            data-testid="waterfall-chart-bar"
            style={{
              left: `${props.active.start_percentage}%`,
              top: "calc(50% - 5px)",
              width: `${props.active.total_percentage}%`,
              height: "10px",
            }}
            className="waterfall-chart__bar absolute"
          >
            <HoverCardTrigger asChild={true}>
              <div className="flex h-full w-full overflow-hidden rounded-sm">
                <div
                  className="h-full bg-gray-500"
                  style={{
                    width: `${props.active.waiting_percentage}%`,
                  }}
                />
                <div
                  className="h-full bg-orange-500"
                  style={{
                    width: `${props.active.dispatching_percentage}%`,
                  }}
                />
                <div
                  className="h-full"
                  style={{
                    width: `${props.active.running_percentage}%`,
                    backgroundColor: outcomeBarColor,
                  }}
                />
              </div>
            </HoverCardTrigger>
          </div>
          <HoverCardContent size="max-lg">
            <div className="charcoal-800">
              <WaterfallPopoverDuration
                text="Limited"
                duration={props.limited.duration}
                variant="limited"
              />
              <WaterfallPopoverDuration
                text="Waiting"
                duration={props.active.waiting_duration}
                variant="waiting"
              />
              <WaterfallPopoverDuration
                text="Dispatching"
                duration={props.active.dispatching_duration}
                variant="dispatching"
              />
              <WaterfallPopoverDuration
                text="Running"
                duration={props.active.running_duration}
                variant="running"
              />
            </div>
          </HoverCardContent>
        </HoverCard>
      )}

      {props.isLastBar && (
        <div
          data-testid="row-duration"
          className="dark-gray monospace absolute m-0 whitespace-nowrap text-xs font-normal"
          style={{
            left: `calc(${props.active.start_percentage + props.active.total_percentage}% + 5px)`,
            top: "calc(50% - 8px)",
          }}
        >
          {props.duration}
        </div>
      )}
    </>
  );
}

type WaterfallRowChartProps = {
  data: WaterfallRowType;
  barContainerPadding: number;
  depth: number;
};

export default function WaterfallRowChart(props: WaterfallRowChartProps) {
  // Because reverse mutates the array, we need to destructure
  // it to get a reversed array without mutating the original array
  const lastValidBar = [...props.data.bars]
    .reverse()
    .find((bar) => bar.active.total_percentage > 0);

  return (
    <div
      className={classNames(
        "-mt-[1px] border-y border-gray-400",
        "relative col-span-3 m-0 box-content w-full bg-white py-0",
        Features.BuildSidebar ? "h-[37.5px]" : "h-[52px]",
      )}
      data-testid="waterfall-chart"
    >
      <div
        className="absolute inset-0 flex items-center pl-[5px]"
        style={{
          paddingRight: `calc(${props.barContainerPadding}px + 1rem)`,
        }}
      >
        <div className="relative h-full w-full">
          {props.data.bars.map((bar, index) => {
            return (
              <MaybeLink
                className="h-full w-full"
                to={
                  Features.BuildSidebar ? bar.job_uuid && resolveJobUrl(bar.job_uuid) : bar.job_url
                }
                key={["bar", bar.job_url].join("-")}
              >
                <WaterfallChartBar
                  {...bar}
                  isLastBar={lastValidBar === bar}
                  duration={index === 0 ? bar.active.total_duration : props.data.duration}
                />
              </MaybeLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}
